import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileError } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, FormProvider, Preloader, ShimmerBox, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { BackButton, Modal } from '@/components';
import ScanPreviewContainer from '@/components/controls/DropScan/ScanPreview/ScanPreviewContainer';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import ChangeFile from '@/modules/MyDocuments/components/ChangeFile/ChangeFile';
import useLettersOfMedicalNecessityForm, {
  LettersOfMedicalNecessityForm,
  LettersOfMedicalNecessityFormField,
} from '@/modules/MyDocuments/hooks/useLettersOfMedicalNecessityForm';
import useSubmitLettersOfMedicalNecessityPopup from '@/modules/MyDocuments/hooks/useSubmitLettersOfMedicalNecessityPopup';
import { useViewLmnItem } from '@/modules/MyDocuments/hooks/useViewLmnItem';
import {
  DocumentTypesEnum,
  LMN_UPLOAD_STORE_KEY,
  LMNPopupMode,
  LMNPopupStep,
} from '@/modules/MyDocuments/myDocuments.types';
import useClaimsSearchParticipantQuery from '@/modules/MyDocuments/queries/useClaimsSearchParticipant.query';
import useUpdateLmnItemQuery from '@/modules/MyDocuments/queries/useUpdateLmnItem.query';
import useLettersOfMedicalNecessityPopupStore, {
  LettersOfMedicalNecessityPopupState,
} from '@/modules/MyDocuments/stores/useLettersOfMedicalNecessityPopup.store';
import { useAuthStore } from '@/modules/user/stores';

import DocumentPreviewPopupForm from './DocumentPreviewPopupForm';

interface Props {
  mode: LMNPopupMode;
  onSetVisible: (value: boolean) => void;
  setStep?: (step: LMNPopupStep) => void;
  onSuccess?: (value: string) => void;
  itemId?: string;
  onEdit?: () => void;
  setIsError?: (value: boolean) => void;
  onFileUploadError?: (error?: FileError) => void;
  onDelete?: (lmnId: string) => void;
  isMobile?: boolean;
  createForClaimId?: string;
  disabledClaimSelect?: boolean;
}

const DocumentPreviewPopup: React.FC<Props> = ({
  mode,
  onSetVisible,
  onSuccess,
  setStep,
  itemId,
  onEdit,
  setIsError,
  onFileUploadError,
  onDelete,
  isMobile,
  createForClaimId,
  disabledClaimSelect,
}) => {
  const { t } = useTranslation();
  const { employeeId } = useAuthStore();
  const { methods } = useLettersOfMedicalNecessityForm({ isViewMode: mode === LMNPopupMode.VIEW });
  const { updateLMN } = useUpdateLmnItemQuery();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const { values, handleReset } = useFilesInDocumentStore(LMN_UPLOAD_STORE_KEY);
  const { sourceState, setSourceState } = useLettersOfMedicalNecessityPopupStore();
  const [isLoading, setIsLoading] = useState(false);
  const { claims } = useClaimsSearchParticipantQuery({
    page: 1,
    perPage: 1,
    disabled: mode === LMNPopupMode.VIEW || !!createForClaimId,
  });
  const { handleCreateLMN, handleSuccessfullyCreated } = useSubmitLettersOfMedicalNecessityPopup({
    onFileUploadError,
  });
  const { lmnItem, isLoading: isViewLoading } = useViewLmnItem({ id: itemId });
  const nextRef = useRef<() => Promise<void>>(undefined);

  const hasAvailableClaims = useMemo(() => !!claims?.length, [claims?.length]);

  const allFiles = useMemo(() => (values?.files?.length ? values?.files : []), [values?.files]);
  const file = useMemo(() => allFiles?.[0], [allFiles]);

  const handleSubmitEdit = useCallback(
    async (formValues: LettersOfMedicalNecessityForm) => {
      if (!itemId) return;
      try {
        setIsLoading(true);
        await updateLMN({
          id: itemId,
          description: formValues[LettersOfMedicalNecessityFormField.description],
          name: formValues[LettersOfMedicalNecessityFormField.name],
        });
        handleAddPermanentSnackbar({
          text: `${formValues[LettersOfMedicalNecessityFormField.name]} ${t('was edited.')}`,
          state: SnackbarState.positive,
          closeIcon: true,
          autoRemove: true,
        });
        onSetVisible(false);
      } finally {
        setIsLoading(false);
      }
    },
    [itemId, updateLMN, handleAddPermanentSnackbar, t, onSetVisible]
  );

  const handleNext = useCallback(
    async (formValues: LettersOfMedicalNecessityForm) => {
      setIsLoading(true);
      const formData: LettersOfMedicalNecessityPopupState = {
        name: formValues[LettersOfMedicalNecessityFormField.name] || '',
        description: formValues[LettersOfMedicalNecessityFormField.description],
        documentType: formValues[LettersOfMedicalNecessityFormField.type] as DocumentTypesEnum,
        employeeId: employeeId,
        claimIds: createForClaimId ? [createForClaimId] : ([] as string[]),
      };

      if (hasAvailableClaims && !createForClaimId && !disabledClaimSelect) {
        setSourceState(formData);
        setStep?.(LMNPopupStep.ASSOCIATE_WITH_CLAIMS);
        setIsLoading(false);
        return;
      }

      try {
        const createdLMN = await handleCreateLMN(formData);
        if (!createdLMN?.id) {
          return;
        }
        onSuccess?.(createdLMN?.id || '');
        handleSuccessfullyCreated(createForClaimId);
        onSetVisible(false);
      } catch {
        nextRef.current = async () => {
          await handleNext(formValues);
        };
        setSourceState({
          ...sourceState,
          onError: async () => {
            await nextRef.current?.();
          },
        });
        setIsError?.(true);
      } finally {
        setIsLoading(false);
      }
    },
    [
      employeeId,
      setSourceState,
      setStep,
      handleCreateLMN,
      onSetVisible,
      setIsError,
      handleSuccessfullyCreated,
      onSuccess,
      hasAvailableClaims,
      sourceState,
      createForClaimId,
      disabledClaimSelect,
    ]
  );

  const handleClose = useCallback(() => {
    handleReset();
    onSetVisible(false);
  }, [onSetVisible, handleReset]);

  const handleDelete = useCallback(() => {
    if (itemId) {
      onSetVisible(false);
      onDelete?.(itemId);
    }
  }, [onSetVisible, onDelete, itemId]);

  const title = useMemo(() => {
    if (mode === LMNPopupMode.UPLOAD) {
      return t('Add new Letter of Medical Necessity');
    }
    if (mode === LMNPopupMode.EDIT) {
      return `${t('Edit')} ${file?.name}`;
    }
    if (mode === LMNPopupMode.VIEW) {
      return file?.name;
    }
  }, [mode, t, file?.name]);

  const nextButtonTitle = useMemo(() => {
    if (isLoading) {
      return <Preloader color="white" />;
    }

    if (createForClaimId) {
      return t('Submit');
    }

    return t('Submit');
  }, [t, isLoading, createForClaimId]);

  const validDates = useMemo(() => {
    if (!lmnItem?.validFrom || !lmnItem?.validTo) return '';
    const fromDate = dayjs(lmnItem.validFrom).format(DEFAULT_DATE_FORMAT);
    const toDate = dayjs(lmnItem.validTo).format(DEFAULT_DATE_FORMAT);
    return `${fromDate} - ${toDate}`;
  }, [lmnItem?.validFrom, lmnItem?.validTo]);

  useEffect(() => {
    if ((mode === LMNPopupMode.VIEW || mode === LMNPopupMode.EDIT) && lmnItem) {
      methods.setValue(
        LettersOfMedicalNecessityFormField.type,
        DocumentTypesEnum.LETTER_OF_MEDICAL_NECESSITY
      );
      methods.setValue(LettersOfMedicalNecessityFormField.name, lmnItem?.name || file?.name || '');
      methods.setValue(LettersOfMedicalNecessityFormField.description, lmnItem?.description || '');
      methods.setValue(LettersOfMedicalNecessityFormField.validDates, validDates);
    }
  }, [mode, file, methods, lmnItem, validDates]);

  useEffect(() => {
    if (mode === LMNPopupMode.UPLOAD && file?.name) {
      methods.setValue(LettersOfMedicalNecessityFormField.name, file?.name || '');
    }
  }, [mode, file?.name, methods]);

  return (
    <Modal
      isCloseButtonHidden={isMobile}
      testId={`lmn-document-preview_${mode.toLocaleLowerCase()}`}
      visible
      onSetVisible={handleClose}
      isFullScreen={isMobile}
    >
      {isMobile ? (
        <Box margin={{ bottom: 'spacing16' }}>
          <BackButton testId="lmn-popup" onClick={handleClose} title="Letters of Medical Necessity" />
        </Box>
      ) : null}

      <Text size="2xl" textAlign={isMobile ? 'start' : 'center'} weight={700} data-testId="title_modal">
        {title}
      </Text>

      <Box
        width={{ max: 'none' }}
        margin={{
          top: isMobile ? 'spacing16' : 'spacing24',
          horizontal: isMobile ? '-14px' : undefined,
        }}
      >
        {(mode === LMNPopupMode.VIEW || mode === LMNPopupMode.EDIT) && isViewLoading ? (
          <Box gap="spacing24">
            <ShimmerBox height="500px" width="100%" />
            <ShimmerBox height="200px" width="100%" />
          </Box>
        ) : (
          <>
            <Box pad={isMobile ? 'spacing16' : 'spacing24'} background="module" round="moduleRound">
              <ChangeFile
                isViewMode={mode === LMNPopupMode.VIEW || mode === LMNPopupMode.EDIT}
                onFileUploadError={onFileUploadError}
                storeKey={LMN_UPLOAD_STORE_KEY}
              />

              <Box margin={{ top: 'spacing24' }}>
                <ScanPreviewContainer
                  isThumbsBottom={isMobile}
                  defaultFiles={allFiles}
                  activeFile={0}
                  isActiveFileShown
                  disableUpload
                  isRemovingDisabled
                  showPagesInPreview
                  isShowTooltip={false}
                  storeKey={LMN_UPLOAD_STORE_KEY}
                />
              </Box>
            </Box>

            <Box
              data-testId="form-wrapper_modal"
              margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
              pad={isMobile ? 'spacing16' : 'spacing24'}
              background="module"
              round="moduleRound"
            >
              <FormProvider {...methods}>
                <DocumentPreviewPopupForm mode={mode} createForClaimId={createForClaimId} />
              </FormProvider>
            </Box>
          </>
        )}
      </Box>

      <Box
        margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
        gap="spacing12"
        direction="row"
        align="center"
      >
        {onDelete && mode === LMNPopupMode.VIEW ? (
          <Box flex={isMobile}>
            <AppButton
              testId="lmn-document-preview_delete"
              width={isMobile ? '100%' : '160px'}
              buttonType="secondary"
              color="red"
              onClick={handleDelete}
            >
              {t('Delete')}
            </AppButton>
          </Box>
        ) : null}

        <Box flex={isMobile} margin={{ left: 'auto' }}>
          <AppButton
            testId="lmn-document-preview_close"
            width={isMobile ? '100%' : '160px'}
            buttonType="secondary"
            onClick={handleClose}
          >
            {t('Close')}
          </AppButton>
        </Box>

        {mode === LMNPopupMode.VIEW && onEdit ? (
          <Box flex={isMobile}>
            <AppButton
              testId="lmn-document-preview_edit"
              width={isMobile ? '100%' : '160px'}
              buttonType="primary"
              onClick={onEdit}
            >
              {t('Edit')}
            </AppButton>
          </Box>
        ) : null}

        {mode === LMNPopupMode.UPLOAD ? (
          <Box flex={isMobile}>
            <AppButton
              testId="lmn-document-preview_next"
              width={isMobile ? '100%' : '160px'}
              buttonType="primary"
              onClick={methods.handleSubmit(handleNext)}
              disabled={isLoading}
            >
              {nextButtonTitle}
            </AppButton>
          </Box>
        ) : null}

        {mode === LMNPopupMode.EDIT ? (
          <Box flex={isMobile}>
            <AppButton
              testId="lmn-document-preview_save"
              width={isMobile ? '100%' : '160px'}
              buttonType="primary"
              onClick={methods.handleSubmit(handleSubmitEdit)}
              disabled={isLoading}
            >
              {isLoading ? <Preloader color="white" /> : t('Save')}
            </AppButton>
          </Box>
        ) : null}
      </Box>
    </Modal>
  );
};

export default DocumentPreviewPopup;
